import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Observa a largura de um elemento montado via callback ref.
 * Retorna [refCallback, isWideEnough].
 * Enquanto não há elemento, assume largo (rótulos visíveis) para evitar estado preso em ícone.
 */
export function useElementMinWidth(
  minWidthPx: number,
): [(node: HTMLElement | null) => void, boolean] {
  const [isWideEnough, setIsWideEnough] = useState(true);
  const observerRef = useRef<ResizeObserver | null>(null);

  const refCallback = useCallback(
    (element: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!element || typeof ResizeObserver === 'undefined') {
        setIsWideEnough(true);
        return;
      }

      const update = (width: number) => {
        setIsWideEnough(width >= minWidthPx);
      };

      update(element.getBoundingClientRect().width);

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        update(entry.contentRect.width);
      });

      observer.observe(element);
      observerRef.current = observer;
    },
    [minWidthPx],
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  return [refCallback, isWideEnough];
}
